import { Injectable, computed, signal } from '@angular/core';
import {
  User, createUserWithEmailAndPassword, onAuthStateChanged, sendPasswordResetEmail,
  signInWithEmailAndPassword, signOut, updateProfile,
} from 'firebase/auth';
import { Unsubscribe, collection, getDocs, limit, onSnapshot, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from './firebase';
import { PosterStore } from './poster-store';
import { PosterSize } from '../poster/poster.model';
import { Dict } from './i18n/en';
import { LangCode } from './i18n/languages';

/** Must match PARTNER_TERMS_VERSION in the backend (partner/partner.ts). */
export const PARTNER_TERMS_VERSION = '2026-10-20';

export interface Partner {
  id: string;
  authId: string;
  name: string;
  businessName?: string | null;
  email: string;
  partnerType: string;
  commission: number;
  balance?: number;
  totalCommission?: number;
  unpaidCommission?: number;
  tourSales?: number;
  subscriptionSales?: number;
  stripeAccountId?: string;
  linkCode?: string;
  termsVersion?: string;
  marketing?: { link: string; linkId: string };
}

export interface PartnerLink {
  code: string;
  kind: 'poster' | 'partner';
  destination: string;
  city: { id: number; name: string; countryCode: string } | null;
  posterSize: PosterSize | null;
  design: string | null;
  /** Missing on posters printed before posters had a language: English. */
  language?: LangCode | null;
  scans?: number;
  scansByDay?: Record<string, number>;
  sales?: number;
  tourSales?: number;
  subscriptionSales?: number;
  commission?: number;
  createdAt?: { toMillis(): number } | null;
}

export interface SaleRow {
  id: string;
  usdCommission: number;
  usdPrice: number;
  date: Date;
  isSubscription: boolean;
  paid: boolean;
  refunded?: boolean;
  linkCode?: string | null;
  countryCode?: string | null;
}

export interface PosterLinkResult {
  code: string;
  url: string;
  claimSecret: string | null;
  partnerId: string | null;
}

export interface SignUpDetails {
  email: string;
  password: string;
  name: string;
  businessName: string;
  partnerType: 'accommodation' | 'other_business' | 'individual';
  agreedToTerms: boolean;
}

export type StripeState = 'loading' | 'none' | 'incomplete' | 'ready' | 'error';

const FRIENDLY_AUTH_ERRORS: Record<string, keyof Dict['errors']> = {
  'auth/email-already-in-use': 'emailInUse',
  'auth/invalid-email': 'invalidEmail',
  'auth/weak-password': 'weakPassword',
  'auth/invalid-credential': 'wrongPassword',
  'auth/wrong-password': 'wrongPassword',
  'auth/user-not-found': 'userNotFound',
  'auth/too-many-requests': 'tooManyRequests',
  'auth/network-request-failed': 'offline',
};

/** What went wrong, in the reader's language (`errors` from the dictionary). */
export function friendlyError(err: any, errors: Dict['errors']): string {
  const code = err?.code as string | undefined;
  if (code && FRIENDLY_AUTH_ERRORS[code]) return errors[FRIENDLY_AUTH_ERRORS[code]];
  if (typeof err?.message === 'string' && code?.startsWith('functions/')) return err.message;
  return errors.generic;
}

@Injectable({ providedIn: 'root' })
export class PortalService {
  readonly user = signal<User | null>(null);
  readonly authReady = signal(false);
  readonly partner = signal<Partner | null>(null);
  /** True once we know whether the signed-in user is a partner. */
  readonly partnerReady = signal(false);
  readonly links = signal<PartnerLink[]>([]);
  readonly stripe = signal<StripeState>('loading');

  readonly isPartner = computed(() => !!this.partner());
  readonly posters = computed(() => this.links().filter(l => l.kind === 'poster'));
  readonly homeLink = computed(() => this.links().find(l => l.kind === 'partner') || null);
  readonly totalScans = computed(() => this.links().reduce((n, l) => n + (l.scans || 0), 0));

  private unsubscribers: Unsubscribe[] = [];
  private readyResolvers: (() => void)[] = [];

  constructor(private store: PosterStore) {
    onAuthStateChanged(auth, user => {
      this.user.set(user);
      this.authReady.set(true);
      this.watch(user);
    });
  }

  /** Resolves once auth and the partner lookup have settled. */
  whenReady(): Promise<void> {
    if (this.authReady() && this.partnerReady()) return Promise.resolve();
    return new Promise(resolve => this.readyResolvers.push(resolve));
  }

  /** Waits for the partner doc to arrive, e.g. straight after sign-up. */
  async waitForPartner(timeoutMs = 15000): Promise<boolean> {
    const until = Date.now() + timeoutMs;
    while (!this.partner() && Date.now() < until) {
      await new Promise(r => setTimeout(r, 100));
    }
    return !!this.partner();
  }

  private settle(): void {
    this.partnerReady.set(true);
    const resolvers = this.readyResolvers;
    this.readyResolvers = [];
    resolvers.forEach(r => r());
  }

  private watch(user: User | null): void {
    this.unsubscribers.forEach(u => u());
    this.unsubscribers = [];
    this.partner.set(null);
    this.links.set([]);
    this.partnerReady.set(false);
    if (!user) {
      this.settle();
      return;
    }

    this.unsubscribers.push(onSnapshot(
      query(collection(db, 'partners'), where('authId', '==', user.uid), limit(1)),
      snap => {
        const doc = snap.docs[0];
        this.partner.set(doc ? ({ ...doc.data(), id: doc.id } as Partner) : null);
        this.settle();
      },
      err => {
        console.error('partner watch failed', err);
        this.settle();
      },
    ));

    this.unsubscribers.push(onSnapshot(
      query(collection(db, 'partner-links'), where('ownerUid', '==', user.uid)),
      snap => {
        const links = snap.docs.map(d => d.data() as PartnerLink);
        links.sort((a, b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0));
        this.links.set(links);
      },
      err => console.error('links watch failed', err),
    ));
  }

  private async call<T>(name: string, data: unknown): Promise<T> {
    const result = await httpsCallable(functions, name)(data);
    return result.data as T;
  }

  createPosterLink(input: { city: { id: number; name: string; countryCode: string }; path: string; size: PosterSize; design: string; language: LangCode }): Promise<PosterLinkResult> {
    return this.call<PosterLinkResult>('createPosterLink', input);
  }

  /** Creates the auth user and the partner, claiming this browser's posters. */
  async signUp(details: SignUpDetails): Promise<{ partnerId: string; claimed: string[] }> {
    if (!this.user()) {
      const cred = await createUserWithEmailAndPassword(auth, details.email.trim(), details.password);
      await updateProfile(cred.user, { displayName: details.name }).catch(() => undefined);
    }
    return this.finishPartnerSetup(details);
  }

  /** For a signed-in user who is not a partner yet (e.g. an app account). */
  async finishPartnerSetup(details: Omit<SignUpDetails, 'email' | 'password'>): Promise<{ partnerId: string; claimed: string[] }> {
    const result = await this.call<{ partnerId: string; claimed: string[] }>('createPartnerAccount', {
      name: details.name.trim(),
      businessName: details.businessName.trim(),
      partnerType: details.partnerType,
      agreedToTerms: details.agreedToTerms,
      claims: this.store.claims(),
    });
    this.store.removeClaimed(result.claimed);
    return result;
  }

  async signIn(email: string, password: string): Promise<void> {
    const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
    // The auth listener may not have run yet: wait for this user's partner lookup.
    const until = Date.now() + 15000;
    while ((this.user()?.uid !== cred.user.uid || !this.partnerReady()) && Date.now() < until) {
      await new Promise(r => setTimeout(r, 50));
    }
    await this.claimStoredPosters();
  }

  /**
   * Hands posters printed on this browser before signing in to the partner. A
   * partner from the old portal (no linkCode) is brought onto the new links too.
   */
  async claimStoredPosters(): Promise<string[]> {
    const partner = this.partner();
    const claims = this.store.claims();
    if (partner && !partner.linkCode) {
      const result = await this.call<{ claimed: string[] }>('createPartnerAccount', { claims });
      this.store.removeClaimed(result.claimed);
      return result.claimed;
    }
    if (!partner || claims.length === 0) return [];
    const result = await this.call<{ claimed: string[] }>('claimPosterLinks', { partnerId: partner.id, claims });
    this.store.removeClaimed(result.claimed);
    return result.claimed;
  }

  async acceptTerms(): Promise<void> {
    const partner = this.partner();
    if (!partner) return;
    await this.call('acceptPartnerTerms', { partnerId: partner.id, agreed: true, termsVersion: PARTNER_TERMS_VERSION });
  }

  resetPassword(email: string): Promise<void> {
    return sendPasswordResetEmail(auth, email.trim());
  }

  signOut(): Promise<void> {
    return signOut(auth);
  }

  async loadSales(partnerId: string): Promise<SaleRow[]> {
    const snap = await getDocs(query(collection(db, 'partners', partnerId, 'month')));
    const rows: SaleRow[] = [];
    for (const month of snap.docs) {
      for (const tx of (month.data()['transactions'] || []) as any[]) {
        rows.push({ ...tx, date: tx.date?.toDate ? tx.date.toDate() : new Date(tx.date) });
      }
    }
    return rows.sort((a, b) => b.date.getTime() - a.date.getTime());
  }

  async refreshStripe(): Promise<void> {
    const partner = this.partner();
    if (!partner) return;
    this.stripe.set('loading');
    try {
      const account = await this.call<any>('getPartnerStripeAccount', { partnerId: partner.id });
      this.stripe.set(!account ? 'none' : account.payouts_enabled ? 'ready' : 'incomplete');
    } catch (err) {
      console.error('stripe account lookup failed', err);
      this.stripe.set('error');
    }
  }

  async openStripe(): Promise<void> {
    const partner = this.partner();
    if (!partner) return;
    const ready = this.stripe() === 'ready';
    const url = await this.call<string | undefined>(
      ready ? 'getPartnerStripeDashboardLink' : 'getPartnerStripeOnboardingLink',
      ready ? { partnerId: partner.id } : { partnerId: partner.id, email: partner.email },
    );
    if (!url) throw new Error('No Stripe link');
    window.location.href = url;
  }
}
